import React, { useCallback, useContext, useEffect, useState } from "react";
import TextInput from "react-autocomplete-input";
import GroupContext from "../../context/group";

const FormAutocomplete = ({
  id,
  field,
  label,
  placeholder,
  validate,
  formatWarning,
  options,
  onRequestOptions,
  emptyMsg,
  isRequired = false,
  autoSelect = true,
  ...other
}) => {
  const { formData, onChange, formErrors, setFormErrors } = useContext(GroupContext);
  const currentValue = formData[field];
  const onFocus = useCallback(
    () => setFormErrors((value) => ({ ...value, [field]: null })),
    [setFormErrors, field]
  );
  const onBlur = useCallback(
    (e) => {
      if (validate) {
        setFormErrors((value) => ({
          ...value,
          [field]: !!e.target.value && !validate(e.target.value) ? formatWarning : null,
        }));
      }
    },
    [validate, setFormErrors, field, formatWarning]
  );

  const onChangeValue = useCallback(
    (newVal) => {
      onChange({
        target: {
          name: field,
          value: newVal,
          type: "text",
        },
      });
    },
    [field, onChange]
  );

  const [value, setValue] = useState(currentValue);
  // When input value is exactly one of the options (like with autofill), automatically set form value
  useEffect(() => {
    if (autoSelect && options?.includes(value)) {
      onChangeValue(value);
    }
  }, [value, options, onChangeValue, autoSelect]);
  // When current form value changes, update input value
  useEffect(() => setValue(currentValue || ""), [currentValue]);

  return (
    <label className="form__input-label form__input-label--autocomplete" htmlFor={id}>
      <span>
        {label}
        {label && isRequired ? " *" : ""}
      </span>
      {formErrors[field] && (
        <span className="form__error-message d-inline">
          {currentValue ? formatWarning : formErrors[field]}
        </span>
      )}
      {!options || options.length || onRequestOptions ? (
        <TextInput
          id={id}
          name={field}
          className="form__input"
          trigger=""
          spacer=""
          minChars={1}
          regex="^.+$"
          Component="input"
          autoComplete="off"
          offsetY={8}
          value={value}
          placeholder={placeholder}
          onFocus={onFocus}
          onBlur={onBlur}
          data-required={isRequired}
          maxOptions={0}
          data-invalid={!options?.includes(value)}
          readOnly={!options && !onRequestOptions}
          matchAny={false}
          {...other}
          onChange={setValue}
          onSelect={onChangeValue}
          onRequestOptions={onRequestOptions}
          options={options || []}
        />
      ) : (
        <>
          <input
            className="form__input"
            name={field}
            readOnly={true}
            value={emptyMsg}
            data-required={isRequired}
            data-invalid="true"
          />
        </>
      )}
    </label>
  );
};

export default FormAutocomplete;
